import React, { useCallback, useEffect, useState } from 'react';

import * as styles from './styles.module.scss';

import {
  createForwardingRule,
  saveForwardingRule,
} from '../../../../shared/services/forwardingRulesService';

import {
  createFormSubmitHandler,
  NvForm,
  NvModal,
} from '@simwood/nv-react-components';
import { useForm } from 'react-hook-form';
import { BsMicrosoftTeams } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import {
  ApiForwardingRule,
  ApiForwardingRuleCreate,
} from '../../../../shared/types/ForwardingRule';
import { ApiPhoneExtensionWithSip } from '../../../../shared/types/PhoneExtension';
import { StoreState } from '../../../store';
import {
  denormalizeForwardingRuleFormValues,
  ForwardingRuleFormValues,
  normalizeForwardingRuleFormDefaults,
} from './forwardingRuleForm';

interface Props {
  extension: ApiPhoneExtensionWithSip;
  forwardingRule?: ApiForwardingRuleCreate;
  isCreate?: boolean;
  onClose: (isCancel: boolean) => void;
  setForwardingRules: React.Dispatch<React.SetStateAction<ApiForwardingRule[]>>;
}

const callOutcomeOptions = [
  // { label: 'Any', value: undefined },
  { label: 'Busy', value: 'BUSY' },
  { label: 'Unanswered', value: 'NOANSWER' },
  { label: 'Failure', value: 'FAILURE' },
] as { label: string; value: string }[];

const ForwardingRuleEdit: React.FC<Props> = ({
  extension,
  forwardingRule: initialForwardingRule,
  isCreate,
  onClose,
  setForwardingRules,
}): JSX.Element => {
  const timeIntervalsById = useSelector(
    (state: StoreState) => state.timeIntervals.timeIntervalsById,
  );
  const activeCustomerId = useSelector(
    (state: StoreState) => state.client.activeCustomer?.id,
  );

  const [isSaving, setIsSaving] = useState(false);

  const timeintervalOptions = Object.values(timeIntervalsById).map((ti) => ({
    label: ti.name,
    value: ti.id,
  }));

  const isPhone = extension.type === 'phone';
  const actionOptions = [
    { label: 'Forward', value: 'FORWARD' },
    { label: 'Follow me', value: 'FOLLOWME' },
  ];
  if (isPhone) {
    actionOptions.push({
      label: 'Send to voicemail',
      value: 'VOICEMAIL',
    });
  }

  const forwardingRule = normalizeForwardingRuleFormDefaults(
    initialForwardingRule,
    {
      timeInterval: timeintervalOptions[0]?.value,
    },
  );

  const {
    handleSubmit,
    control,
    watch,
    clearErrors,
    trigger,
    formState: { isDirty },
  } = useForm<ForwardingRuleFormValues>({
    mode: 'onBlur',
    defaultValues: forwardingRule,
  });

  const voicemailSelected = watch('action') === 'VOICEMAIL';
  const isTeamsOC = watch('platform') === 'teams';
  const currentKey = watch('key');

  const save = useCallback(
    async (modified: ForwardingRuleFormValues) => {
      setIsSaving(true);
      try {
        if (modified) {
          const forwardingRule: ApiForwardingRuleCreate =
            denormalizeForwardingRuleFormValues(modified);

          if (isCreate) {
            const res = await createForwardingRule(
              activeCustomerId!,
              extension.id,
              forwardingRule,
            );
            setForwardingRules((rules) => [...rules, res]);
          } else {
            const res = await saveForwardingRule(
              activeCustomerId!,
              extension.id,
              forwardingRule as ApiForwardingRule,
            );
            setForwardingRules((rules) => {
              const idx = rules.findIndex((fr) => fr.id === res.id);

              return [...rules.slice(0, idx), res, ...rules.slice(idx + 1)];
            });
          }

          onClose(false);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [activeCustomerId, extension.id, isCreate, onClose, setForwardingRules],
  );

  useEffect(() => {
    if (voicemailSelected) {
      clearErrors('destination');
      return;
    }

    trigger('destination');
  }, [clearErrors, trigger, voicemailSelected]);

  return (
    <NvModal.Form
      open={true}
      title={isCreate ? 'New Forwarding Rule' : 'Edit Forwarding Rule'}
      disabled={(!isCreate && !isDirty) || isSaving}
      closeOnSubmit
      onSubmit={createFormSubmitHandler(handleSubmit, save)}
      setOpen={(open) => {
        if (!open) {
          onClose(true);
        }
      }}
      submitText={isCreate ? 'Create' : 'Save'}
    >
      <NvForm.Select
        name='timeInterval'
        control={control}
        labelText='During time interval'
        items={timeintervalOptions}
        disabled={isTeamsOC}
      />
      <NvForm.Select
        name='callOutcome'
        control={control}
        labelText='If call status is'
        nullLabel='Any'
        items={callOutcomeOptions}
        disabled={isTeamsOC}
      />
      <NvForm.Select
        name='action'
        control={control}
        labelText='Action'
        items={actionOptions}
        disabled={isTeamsOC}
      />

      <span className={styles.input}>
        {isTeamsOC && <BsMicrosoftTeams />}
        <NvForm.Input
          name='destination'
          control={control}
          labelText='To'
          disabled={voicemailSelected || isTeamsOC}
          required={!voicemailSelected}
          transform={{
            input: (x) => x,
            output: (x) => x?.replace(/\D/, '') || '',
          }}
          inputProps={{ maxLength: 128, minLength: 3 }}
        />
      </span>

      {isPhone && (
        <NvForm.Checkbox
          name='applyGroup'
          control={control}
          labelText='Apply to queue/group calls'
          disabled={voicemailSelected}
          kind='checkbox'
        />
      )}
      <NvForm.Checkbox
        name='preserveId'
        control={control}
        labelText='Preserve caller ID'
        disabled={voicemailSelected}
        kind='checkbox'
      />
      <NvForm.Checkbox
        name='screeningEnabled'
        control={control}
        labelText='Enable call screening'
        disabled={voicemailSelected}
        kind='checkbox'
      />
      <NvForm.Checkbox
        name='externalOnly'
        control={control}
        labelText='Apply to external calls only'
        kind='checkbox'
      />
      <NvForm.Input
        name='key'
        control={control}
        labelText='Assigned key'
        type='number'
        transform={{
          input: (x) => (x !== undefined ? String(x) : ''),
          output: (x) => {
            if (!x) return '';
            const key = Number(x?.replace(/\D/, ''));
            if ((key < 1 || key > 99) && key) return currentKey;
            return String(key);
          },
        }}
        placeholder='1-99'
      />
    </NvModal.Form>
  );
};

export default ForwardingRuleEdit;
